import { Box, Flex, Text } from "rebass/styled-components";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { useRecoilState, useRecoilValue } from "recoil";

import { BaseSection } from "./styledComponents";
import { TitleAndLoadouts } from "./TitleAndLoadouts";
import { Moment } from "./Moment";
import { Button } from "../Button";
import { TLoadout } from "MVVM/Views/CreateMontage/@types";
import { FinalTouches } from "./FinalTouches";
import { QuickEditViewModel } from "./ViewModel";
import { LoadingSpinner } from "../Lottie";
import theme from "theme/themeThree";
import {
  StyledIcon,
  StyledRemixButton,
} from "MVVM/Views/Studio/RemixView/Components/Pinned";
import {
  QuickEditPreferredLoadout,
  QuickEditPreferredThumbnail,
  QuickEditTitle,
} from "MVVM/State/quickEdit";
import remixProcessing from "../../../animations/remixing.json";
import Lottie from "lottie-react";
import { getMatchHistoryGameEnum } from "MVVM/Views/MatchHistory/utils";
import { getPlayerGameIdByGame } from "MVVM/Views/MatchHistory/utils";
import { useUser } from "MVVM/Hooks/useUser";
import { analytics } from "MVVM/Analytics";
import { IQuickEditMoment } from "./@types";

const TOTAL_STEPS = 2;

export const QuickEdit = ({
  momentId,
  closeModal,
  onCreate,
}: {
  momentId: string;
  closeModal: () => void;
  onCreate?: () => void;
}) => {
  const { allstarUser } = useUser();
  const [step, setStep] = useState<number>(1);
  const preferredLoadout = useRecoilValue(QuickEditPreferredLoadout);
  const [selectedLoadout, setSelectedLoadout] = useState<TLoadout | null>(
    preferredLoadout,
  );
  const [thumbnailKey, setThumbnailKey] = useState<string>("");
  const [preferredThumbnail, setPreferredThumbnail] = useRecoilState(
    QuickEditPreferredThumbnail,
  );
  const [title, setTitle] = useRecoilState(QuickEditTitle);
  const [finalizing, setFinalizing] = useState<boolean>(false);
  const [selectedFinalTouches, setSelectedFinalTouches] = useState<string[]>(
    [],
  );
  const [isOwnMoment, setIsOwnMoment] = useState<boolean>(false);
  const { moment, momentCreateClip, mediaUploadUrl, loadouts, campaignAlert } =
    QuickEditViewModel(momentId);

  useEffect(() => {
    const relevantPlayerGameId = getPlayerGameIdByGame(
      getMatchHistoryGameEnum(moment?.game),
      allstarUser,
    );
    setIsOwnMoment(relevantPlayerGameId === moment?.player?.gameId);
  }, [moment, allstarUser]);

  useEffect(() => {
    if (finalizing) {
      setTimeout(() => {
        closeModal();
      }, 5000);
    }
  }, [finalizing, onCreate, closeModal]);

  useEffect(() => {
    analytics.track("Quick Edit Display", {});
  }, []);

  const handleClick = (moment: IQuickEditMoment) => {
    if (!loadouts) return;
    if (step === TOTAL_STEPS) {
      if (isOwnMoment) setFinalizing(true);

      setFinalizing(true);
      let loadoutId = selectedLoadout?._id;
      if (selectedLoadout?.name === "Surprise Me") {
        const randomLoadout =
          loadouts[Math.floor(Math.random() * loadouts.length)];
        loadoutId = randomLoadout._id;
      }
      if (selectedLoadout?.name === "No Loadout") {
        loadoutId = undefined;
      }

      momentCreateClip({
        variables: {
          momentCreateClipInput: {
            ids: [momentId],
            matchId: moment.match.id,
            effectsOverrides: {
              thumbnail: thumbnailKey ?? preferredThumbnail,
              loadout: loadoutId,
              rapid: selectedFinalTouches.includes("rapidFire"),
              contextCameras: selectedFinalTouches.includes("contextCams"),
              applySignatureMusic: selectedFinalTouches.includes("music"),
              applySignatureCards: selectedFinalTouches.includes("signature"),
              ...(title && { title }),
            },
          },
        },
      });

      setPreferredThumbnail(thumbnailKey);
      analytics.track("Quick Edit Create", {
        thumbnailUploaded: !!thumbnailKey,
        loadoutSelected: !!selectedLoadout?._id,
        signatureEnabled: selectedFinalTouches.includes("signature"),
        musicEnabled: selectedFinalTouches.includes("music"),
        rapidFireEnabled: selectedFinalTouches.includes("rapidFire"),
        contextCamsEnabled: selectedFinalTouches.includes("contextCams"),
        titleEdited: title !== moment?.title,
      });

      onCreate?.();
      if (!isOwnMoment) closeModal();
    } else {
      analytics.track("Quick Edit Next", {});

      setStep(step + 1);
    }
  };

  if (!moment)
    return (
      <Container
        alignItems={"center"}
        justifyContent={"center"}
        minHeight={650}
      >
        <LoadingSpinner height={25} width={25} />
      </Container>
    );

  if (finalizing) {
    return (
      <Container
        alignItems={"center"}
        justifyContent={"center"}
        flexDirection={"column"}
        minHeight={450}
        maxHeight={300}
      >
        <Text variant="text.headingXSmall" pb={3}>
          Your clip is on the way...
        </Text>
        <Text variant="text.bodySmall" color={theme.colors.chalk}>
          Come back here or visit your profile in a few minutes to watch it!
        </Text>
        <Lottie
          animationData={remixProcessing}
          loop={true}
          style={{ height: 200, width: 200 }}
        />
      </Container>
    );
  }

  return (
    <Container minHeight={300} flexDirection={"column"}>
      <BaseSection
        flexDirection={"column"}
        style={{ gap: 12 }}
        justifyContent={"center"}
        alignItems={"start"}
      >
        <Text variant="text.headingXSmall">Customize Your Clip: </Text>
        <Moment moment={moment} />
        {campaignAlert && (
          <Text
            variant="text.labelXSmall"
            color={theme.colors.error}
            style={{
              fontWeight: "normal",
            }}
          >
            {campaignAlert}
          </Text>
        )}
      </BaseSection>
      {step === 1 && (
        <TitleAndLoadouts
          moment={moment}
          selectedLoadout={selectedLoadout}
          setSelectedLoadout={setSelectedLoadout}
          setTitle={setTitle}
          title={title}
          loadouts={loadouts ?? []}
        />
      )}
      {step === 2 && (
        <FinalTouches
          setThumbnailKey={setThumbnailKey}
          selectedFinalTouches={selectedFinalTouches}
          setSelectedFinalTouches={setSelectedFinalTouches}
          mediaUploadUrl={mediaUploadUrl}
          isOwnMoment={isOwnMoment}
          moment={moment}
        />
      )}
      <Controls>
        <Gradient currentStep={step} totalSteps={TOTAL_STEPS} />
        <Button
          classification="roundedFilledAlt"
          size="small"
          onClick={() => closeModal()}
          flex={true}
        >
          Cancel
        </Button>
        <Flex flexDirection={"row"} style={{ gap: 12 }}>
          {step > 1 && (
            <Button
              classification="roundedFilledAlt"
              size="small"
              onClick={() => setStep(step - 1)}
              flex={true}
            >
              Back
            </Button>
          )}

          {step === TOTAL_STEPS ? (
            <>
              {moment?.clip?.id ? (
                <StyledRemixButton
                  size="small"
                  disabled={finalizing}
                  onClick={() => handleClick(moment)}
                  classification="remixFeed"
                >
                  <StyledIcon />
                  Remix
                </StyledRemixButton>
              ) : (
                <Button
                  classification="roundedFilled"
                  size="small"
                  disabled={finalizing}
                  onClick={() => handleClick(moment)}
                  flex={true}
                >
                  Create
                </Button>
              )}
            </>
          ) : (
            <Button
              classification="roundedFilled"
              size="small"
              disabled={finalizing}
              onClick={() => handleClick(moment)}
              flex={true}
            >
              Next
            </Button>
          )}
        </Flex>
      </Controls>
    </Container>
  );
};

const Gradient = styled(Box)<{ currentStep: number; totalSteps: number }>`
  height: 4px;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  background: ${({ currentStep, totalSteps }) =>
    currentStep === totalSteps
      ? `linear-gradient(to right, ${theme.colors.envy} 50%, ${theme.colors.chalkAlpha20} 50%)`
      : theme.colors.chalkAlpha20};
`;

const Controls = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  max-height: 100px;
  padding: 24px;
  position: relative;
`;

const Container = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 12px;
  max-width: 500px;
  min-width: 350px;
  position: relative;
  width: 90vw;
`;
